<template>
  <div class="play-multiplayer w-[100lvw] h-[100lvh] bg-cw-green flex justify-center items-center">
    <div class="fixed top-[30px] left-[30px]">
      <BaseButton background-color="var(--color-cw-olive)" border-color="#323617" class="w-10 md:w-11 lg:w-12 h-10 md:h-9 lg:h-10 justify-center" @click="leaveGame">
        <ArrowLeftIcon />
      </BaseButton>
    </div>
    <div class="flex flex-col gap-y-12 md:gap-y-14 lg:gap-y-16 justify-center items-center md:scale-150">
      <div class="flex flex-col gap-y-6 items-center">
        <div class="flex flex-col gap-y-0.5 items-center">
          <div class="flex items-center">
            <h1 class="text-lg font-bold">Finding opponents</h1>
            <span class="dots text-lg font-bold" />
          </div>
          <p class="text-sm font-medium text-cw-green-alt">Minimum of 2 players</p>
        </div>
        <div class="flex flex-wrap gap-x-6 gap-y-3 lg:gap-y-5 lg:max-w-[300px] justify-center">
          <div v-for="opponent in opponents" :key="opponent.id" class="flex flex-col gap-y-1 lg:gap-y-1.5 items-center">
            <BaseAvatar :src="opponent.avatarUrl" />
            <p class="text-[10px] font-bold">{{ opponent.username || 'Matching...' }}</p>
          </div>
        </div>
      </div>
      <BaseButton
        v-if="isTurn"
        background-color="#1AA570"
        border-color="var(--color-cw-green-border-shadow)"
        class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5"
        :disabled="allPlayers!.length < MIN_PLAYERS"
        @click="startGame"
      >
        <p class="w-full !text-xs xl:!text-sm">Start game</p>
      </BaseButton>
      <div v-else class="p-4 rounded-md bg-cw-green-alt">
        <p class="text-xs font-medium text-[#A3C2B6]">Waiting for host to start the game...</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useGameStore } from '@/stores/game'
import { AppWindowEvent, GameEvent } from '@/types/Events'
import useSocket from '@/composables/useSocket'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useHelpers } from '@/composables/useHelpers'
import { storeToRefs } from 'pinia'
import ArrowLeftIcon from '@/assets/svgs/icons/arrow-fat-left.svg'

const socket = useSocket()
const { createDummyPlayer, handleCustomError, sendParentWindowMessage } = useHelpers()

const showLoader = inject('showLoader') as InjectedFunctionT
const hideLoader = inject('hideLoader') as InjectedFunctionT

const { allPlayers, MIN_PLAYERS, MAX_PLAYERS, isTurn } = storeToRefs(useGameStore())

const opponents = computed(() => {
  const players = allPlayers.value || []
  const currentPlayerCount = players.length
  const dummyPlayersNeeded = MAX_PLAYERS.value - currentPlayerCount

  const dummyPlayers = Array.from({ length: dummyPlayersNeeded }, (_, index) =>
    createDummyPlayer({
      id: `dummy-${index}`,
      username: `Waiting`,
      firstName: `Waiting`,
      lastName: ``,
      avatarUrl: '',
      points: 0,
      isLetoUser: false,
      status: 'online',
      hand: [],
      type: 'human',
      actionTimeout: -1,
      reconnectTimeout: -1,
      school: null,
      email: null
    })
  )

  return [...players, ...dummyPlayers]
})

const startGame = async () => {
  try {
    showLoader()
    await socket.sendEventAsync(GameEvent.StartGame)
    sendParentWindowMessage(AppWindowEvent.StartGame, { isMultiplayer: true })
  } catch (error) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

const leaveGame = async () => {
  try {
    showLoader()
    await socket.sendEventAsync(GameEvent.LeaveGame)
    useGameStore().resetGame()
  } catch (error) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}
</script>

<style scoped>
.play-multiplayer {
  background-color: var(--color-cw-green);
  background-image: url('@/assets/images/noise-texture.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
</style>
